import { FormEvent, useEffect, useState } from "react";
import { AlertTriangle, Save, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getDistrictsByCity, getPostalCode, taiwanCities } from "@/data/taiwanAddress";
import AdminLayout from "./AdminLayout";
import {
  AdminUser,
  getAdminCompanyProfile,
  getCurrentAdmin,
  logout,
  saveAdminCompanyProfile,
  uploadAdminCompanyProfileBrandImage,
} from "./api";

type FormState = {
  CompanyName: string;
  ServicePhone: string;
  ServicePhoneNote: string;
  ServiceHours: string;
  ServiceEmail: string;
  AddressCity: string;
  AddressDistrict: string;
  AddressLine: string;
  ContactPhone: string;
  SmsPhone: string;
  Copyright: string;
  LineId: string;
  LineUrl: string;
  FacebookUrl: string;
  InstagramUrl: string;
  YoutubeUrl: string;
  LogoUrl: string;
  FaviconUrl: string;
  BookingPublicLookaheadDays: string;
  IsMaintenanceMode: boolean;
  MaintenanceMessage: string;
};

const emptyForm: FormState = {
  CompanyName: "",
  ServicePhone: "",
  ServicePhoneNote: "",
  ServiceHours: "",
  ServiceEmail: "",
  AddressCity: "",
  AddressDistrict: "",
  AddressLine: "",
  ContactPhone: "",
  SmsPhone: "",
  Copyright: "",
  LineId: "",
  LineUrl: "",
  FacebookUrl: "",
  InstagramUrl: "",
  YoutubeUrl: "",
  LogoUrl: "/images/logo-mark.png",
  FaviconUrl: "/static/image/favicon.ico",
  BookingPublicLookaheadDays: "180",
  IsMaintenanceMode: false,
  MaintenanceMessage: "",
};

function RequiredMark() {
  return <span className="ml-1 text-[#c3423f]" aria-hidden="true">*</span>;
}

const inputClass = "h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";

export default function AdminCompanyProfile() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [message, setMessage] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    async function init() {
      try {
        const me = await getCurrentAdmin();
        if (!alive) return;
        setCurrentUser(me.User);
        const data = await getAdminCompanyProfile();
        if (!alive) return;
        const profile = data.CompanyProfile;
        setForm({
          CompanyName: profile.CompanyName,
          ServicePhone: profile.ServicePhone,
          ServicePhoneNote: profile.ServicePhoneNote || "",
          ServiceHours: profile.ServiceHours,
          ServiceEmail: profile.ServiceEmail,
          AddressCity: profile.AddressCity || "",
          AddressDistrict: profile.AddressDistrict || "",
          AddressLine: profile.AddressLine || "",
          ContactPhone: profile.ContactPhone || "",
          SmsPhone: profile.SmsPhone || "",
          Copyright: profile.Copyright || "",
          LineId: profile.LineId || "",
          LineUrl: profile.LineUrl || "",
          FacebookUrl: profile.FacebookUrl || "",
          InstagramUrl: profile.InstagramUrl || "",
          YoutubeUrl: profile.YoutubeUrl || "",
          LogoUrl: profile.LogoUrl && profile.LogoUrl !== "/images/logo.png" ? profile.LogoUrl : "/images/logo-mark.png",
          FaviconUrl: profile.FaviconUrl || "/static/image/favicon.ico",
          BookingPublicLookaheadDays: String(profile.BookingPublicLookaheadDays || 180),
          IsMaintenanceMode: profile.IsMaintenanceMode ?? false,
          MaintenanceMessage: profile.MaintenanceMessage || "",
        });
        setUpdatedAt(profile.UpdatedAt || null);
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "公司資料載入失敗。");
        navigate("/admin-login", { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    }
    void init();
    return () => {
      alive = false;
    };
  }, [navigate]);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function setField<Key extends keyof FormState>(key: Key, value: FormState[Key]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function handleCityChange(city: string) {
    setForm((current) => ({ ...current, AddressCity: city, AddressDistrict: "" }));
  }

  async function handleBrandImageUpload(field: "LogoUrl" | "FaviconUrl", files: FileList | null) {
    const file = files?.[0];
    if (!file) return;

    const setUploading = field === "LogoUrl" ? setUploadingLogo : setUploadingFavicon;
    setUploading(true);
    setMessage("");
    try {
      const url = await uploadAdminCompanyProfileBrandImage(file);
      setField(field, url);
      setMessage(field === "LogoUrl" ? "Logo 圖片已上傳，請記得儲存公司資料。" : "Favicon 圖片已上傳，請記得儲存公司資料。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "圖片上傳失敗。");
    } finally {
      setUploading(false);
    }
  }

  const formDistricts = getDistrictsByCity(form.AddressCity);
  const formPostalCode = getPostalCode(form.AddressCity, form.AddressDistrict);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.CompanyName.trim()) { setMessage("請輸入公司名稱。"); return; }
    if (!form.ServicePhone.trim()) { setMessage("請輸入客服專線。"); return; }
    if (!form.ServiceHours.trim()) { setMessage("請輸入客服時間。"); return; }
    if (!form.ServiceEmail.trim()) { setMessage("請輸入客服 Email。"); return; }
    if (!form.AddressCity.trim()) { setMessage("請選擇公司地址的縣市。"); return; }
    if (!form.AddressDistrict.trim()) { setMessage("請選擇公司地址的鄉鎮市區。"); return; }
    if (!form.AddressLine.trim()) { setMessage("請輸入公司地址的詳細地址。"); return; }

    const bookingLookaheadDays = Number(form.BookingPublicLookaheadDays);
    if (!Number.isInteger(bookingLookaheadDays) || bookingLookaheadDays < 7 || bookingLookaheadDays > 365) {
      setMessage("前台開放預約天數需介於 7 到 365 天。");
      return;
    }

    setSaving(true);
    setMessage("");
    try {
      const saved = await saveAdminCompanyProfile({
        CompanyName: form.CompanyName.trim(),
        ServicePhone: form.ServicePhone.trim(),
        ServicePhoneNote: form.ServicePhoneNote.trim() || undefined,
        ServiceHours: form.ServiceHours.trim(),
        ServiceEmail: form.ServiceEmail.trim(),
        AddressCity: form.AddressCity,
        AddressDistrict: form.AddressDistrict,
        AddressPostalCode: formPostalCode || undefined,
        AddressLine: form.AddressLine.trim(),
        ContactPhone: form.ContactPhone.trim() || undefined,
        SmsPhone: form.SmsPhone.trim() || undefined,
        Copyright: form.Copyright.trim() || undefined,
        LineId: form.LineId.trim() || undefined,
        LineUrl: form.LineUrl.trim() || undefined,
        FacebookUrl: form.FacebookUrl.trim() || undefined,
        InstagramUrl: form.InstagramUrl.trim() || undefined,
        YoutubeUrl: form.YoutubeUrl.trim() || undefined,
        LogoUrl: form.LogoUrl.trim() || undefined,
        FaviconUrl: form.FaviconUrl.trim() || undefined,
        BookingPublicLookaheadDays: bookingLookaheadDays,
        IsMaintenanceMode: form.IsMaintenanceMode,
        MaintenanceMessage: form.MaintenanceMessage.trim() || undefined,
      });
      setUpdatedAt(saved.CompanyProfile.UpdatedAt || null);
      setMessage("公司基本資料已儲存。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "公司資料儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout sectionLabel="網站設定" title="公司基本資料" user={currentUser} onLogout={handleLogout}>
      <form onSubmit={handleSubmit}>
        {message && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

        {loading ? (
          <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-10 text-center text-[#59665f]">載入中...</div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-5">
              <section className={`rounded-md border p-4 ${form.IsMaintenanceMode ? "border-[#f2c9c6] bg-[#fdf3f2]" : "border-[#dfe6e1] bg-white"}`}>
                <div className="mb-1 flex items-center gap-2">
                  <AlertTriangle className={form.IsMaintenanceMode ? "text-[#9a2d2d]" : "text-[#9a8a7a]"} size={18} />
                  <h2 className="text-lg font-bold text-[#203d32]">全站維護模式（軟性維護）</h2>
                </div>
                <p className="mb-4 text-xs text-[#9a8a7a]">
                  開啟後，前台商品瀏覽、搜尋、加入購物車等功能<b>不受影響</b>，只有下單結帳、會員註冊登入、預約、聯絡表單等會寫入資料的操作會暫停並顯示提醒（畫面右下角浮動提示卡）。後台管理完全不受影響，隨時可以回來這裡關閉。適合系統維護、程式更新期間使用。
                </p>
                <label className="flex cursor-pointer items-center justify-between gap-4 rounded-md border border-[#dfe6e1] bg-white p-3">
                  <span>
                    <span className="block text-sm font-bold text-[#315746]">開啟維護模式</span>
                    <span className="block text-xs font-normal text-[#7b877f]">{form.IsMaintenanceMode ? "前台下單、註冊登入等操作目前暫停中" : "前台目前正常對外開放"}</span>
                  </span>
                  <span className="relative inline-flex h-6 w-11 shrink-0 items-center">
                    <input
                      checked={form.IsMaintenanceMode}
                      className="peer sr-only"
                      type="checkbox"
                      onChange={(event) => setField("IsMaintenanceMode", event.target.checked)}
                    />
                    <span className="pointer-events-none absolute inset-0 rounded-full bg-[#cfd8d2] transition peer-checked:bg-[#9a2d2d]" />
                    <span className="pointer-events-none absolute left-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
                  </span>
                </label>
                <label className="mt-3 grid gap-2 text-sm font-bold text-[#315746]">
                  <span>維護說明文字</span>
                  <textarea
                    className="min-h-[80px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                    placeholder="系統維護中，商品瀏覽不受影響，預計於今日 18:00 恢復下單服務，造成不便敬請見諒。（留空則顯示預設文字）"
                    value={form.MaintenanceMessage}
                    onChange={(event) => setField("MaintenanceMessage", event.target.value)}
                  />
                </label>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-1 text-lg font-bold text-[#203d32]">預約顯示設定</h2>
                <p className="mb-4 text-xs text-[#9a8a7a]">
                  控制前台官網可顯示與可送出的預約日期範圍。後台可以建立更久的日期，但前台只會開放這個天數內的日期。
                </p>
                <div className="grid gap-4 sm:grid-cols-[minmax(0,240px)_minmax(0,1fr)] sm:items-start">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>前台開放預約天數</span>
                    <input
                      className={inputClass}
                      max={365}
                      min={7}
                      type="number"
                      value={form.BookingPublicLookaheadDays}
                      onChange={(event) => setField("BookingPublicLookaheadDays", event.target.value)}
                    />
                  </label>
                  <div className="rounded-md border border-[#edf0ee] bg-[#f8faf8] p-3 text-xs leading-6 text-[#59665f]">
                    <p className="font-bold text-[#315746]">建議值：90 到 180 天</p>
                    <p>範圍限制：7 到 365 天。設定太短會讓使用者可選日期太少；設定太長時，請先確認國定假日、補班日與門市特殊休假都已維護。</p>
                  </div>
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">公司名稱</h2>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>公司／品牌名稱<RequiredMark /></span>
                  <input className={inputClass} value={form.CompanyName} onChange={(event) => setField("CompanyName", event.target.value)} />
                </label>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-1 text-lg font-bold text-[#203d32]">品牌識別</h2>
                <p className="mb-4 text-xs text-[#9a8a7a]">前台與後台共用 Logo 與 favicon。可直接上傳圖片，也可手動填公開路徑，例如 /images/logo-mark.png 或 /uploads/brand/logo.png。</p>
                <div className="grid gap-4 sm:grid-cols-[180px_minmax(0,1fr)]">
                  <div className="grid gap-3">
                    <div className="flex h-28 items-center justify-center rounded-md border border-[#dfe6e1] bg-[#f6f7f5] p-3">
                      {form.LogoUrl ? <img className="max-h-full max-w-full object-contain" src={form.LogoUrl} alt="Logo 預覽" /> : <span className="text-xs text-[#9a8a7a]">尚未設定</span>}
                    </div>
                    <div className="flex items-center gap-3 rounded-md border border-[#dfe6e1] bg-[#f6f7f5] p-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-[#dfe6e1] bg-white p-1.5">
                        {form.FaviconUrl ? <img className="max-h-full max-w-full object-contain" src={form.FaviconUrl} alt="Favicon 預覽" /> : null}
                      </div>
                      <span className="text-xs font-bold text-[#7b877f]">Favicon 預覽</span>
                    </div>
                  </div>
                  <div className="grid gap-4">
                    <label className="grid gap-2 text-sm font-bold text-[#315746]">
                      <span>Logo 圖片路徑</span>
                      <input className={inputClass} placeholder="/images/logo-mark.png" value={form.LogoUrl} onChange={(event) => setField("LogoUrl", event.target.value)} />
                    </label>
                    <label className="inline-flex h-10 w-fit cursor-pointer items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2]">
                      <Upload size={16} />
                      {uploadingLogo ? "Logo 上傳中..." : "上傳 Logo"}
                      <input
                        accept="image/jpeg,image/png,image/webp,image/gif,image/x-icon,.ico"
                        className="hidden"
                        disabled={uploadingLogo}
                        type="file"
                        onChange={(event) => {
                          void handleBrandImageUpload("LogoUrl", event.target.files);
                          event.target.value = "";
                        }}
                      />
                    </label>
                    <label className="grid gap-2 text-sm font-bold text-[#315746]">
                      <span>Favicon 圖片路徑</span>
                      <input className={inputClass} placeholder="/static/image/favicon.ico 或 /images/logo.png" value={form.FaviconUrl} onChange={(event) => setField("FaviconUrl", event.target.value)} />
                    </label>
                    <label className="inline-flex h-10 w-fit cursor-pointer items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2]">
                      <Upload size={16} />
                      {uploadingFavicon ? "Favicon 上傳中..." : "上傳 Favicon"}
                      <input
                        accept="image/jpeg,image/png,image/webp,image/gif,image/x-icon,.ico"
                        className="hidden"
                        disabled={uploadingFavicon}
                        type="file"
                        onChange={(event) => {
                          void handleBrandImageUpload("FaviconUrl", event.target.files);
                          event.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">客服聯絡資訊</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>客服專線<RequiredMark /></span>
                    <input className={inputClass} placeholder="0800-123-456" value={form.ServicePhone} onChange={(event) => setField("ServicePhone", event.target.value)} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>專線備註</span>
                    <input className={inputClass} placeholder="免付費（選填）" value={form.ServicePhoneNote} onChange={(event) => setField("ServicePhoneNote", event.target.value)} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746] sm:col-span-2">
                    <span>客服時間<RequiredMark /></span>
                    <input className={inputClass} placeholder="週一至週五 09:00 - 18:00" value={form.ServiceHours} onChange={(event) => setField("ServiceHours", event.target.value)} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746] sm:col-span-2">
                    <span>客服 Email<RequiredMark /></span>
                    <input className={inputClass} type="email" placeholder="service@fuling.com" value={form.ServiceEmail} onChange={(event) => setField("ServiceEmail", event.target.value)} />
                  </label>
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-1 text-lg font-bold text-[#203d32]">公司地址</h2>
                <p className="mb-4 text-xs text-[#9a8a7a]">這裡是前台 Footer 顯示的單一聯絡地址，跟「營運據點」的門市地址是不同資料，門市地址請至「營運據點」管理。</p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>縣市<RequiredMark /></span>
                    <select className={`${inputClass} bg-white`} value={form.AddressCity} onChange={(event) => handleCityChange(event.target.value)}>
                      <option value="">請選擇縣市</option>
                      {taiwanCities.map((city) => <option key={city} value={city}>{city}</option>)}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>鄉鎮市區<RequiredMark /></span>
                    <select
                      className={`${inputClass} bg-white`}
                      disabled={!form.AddressCity}
                      value={form.AddressDistrict}
                      onChange={(event) => setField("AddressDistrict", event.target.value)}
                    >
                      <option value="">{form.AddressCity ? "請選擇鄉鎮市區" : "請先選擇縣市"}</option>
                      {formDistricts.map((district) => <option key={district.name} value={district.name}>{district.name}</option>)}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>郵遞區號</span>
                    <input className={`${inputClass} bg-[#f6f7f5] text-[#7b877f]`} placeholder="選擇鄉鎮市區後自動帶出" readOnly value={formPostalCode} />
                  </label>
                </div>
                <label className="mt-4 grid gap-2 text-sm font-bold text-[#315746]">
                  <span>詳細地址<RequiredMark /></span>
                  <input className={inputClass} placeholder="松高路 88 號" value={form.AddressLine} onChange={(event) => setField("AddressLine", event.target.value)} />
                </label>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-1 text-lg font-bold text-[#203d32]">其他聯絡電話</h2>
                <p className="mb-4 text-xs text-[#9a8a7a]">兩者皆為選填，留空則不會顯示在前台。</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>聯絡電話（公司代表號）</span>
                    <input className={inputClass} placeholder="02-1234-5678" value={form.ContactPhone} onChange={(event) => setField("ContactPhone", event.target.value)} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>簡訊服務號碼</span>
                    <input className={inputClass} placeholder="0912-345-678" value={form.SmsPhone} onChange={(event) => setField("SmsPhone", event.target.value)} />
                  </label>
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-1 text-lg font-bold text-[#203d32]">頁尾版權宣告</h2>
                <p className="mb-4 text-xs text-[#9a8a7a]">顯示在前台網站頁尾最下方，留空則顯示預設的公司名稱版權文字。</p>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>版權宣告文字</span>
                  <input className={inputClass} placeholder="Copyright © 富聆助聽器｜美律富聆股份有限公司 All Rights Reserved." value={form.Copyright} onChange={(event) => setField("Copyright", event.target.value)} />
                </label>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">LINE 官方帳號</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>LINE ID</span>
                    <input className={inputClass} placeholder="@fulinghealth" value={form.LineId} onChange={(event) => setField("LineId", event.target.value)} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>LINE 加好友網址</span>
                    <input className={inputClass} placeholder="https://line.me/R/ti/p/@fulinghealth" value={form.LineUrl} onChange={(event) => setField("LineUrl", event.target.value)} />
                  </label>
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">社群連結</h2>
                <p className="mb-3 text-xs text-[#9a8a7a]">留空的社群連結不會顯示在前台 Footer 的社群圖示上。</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>Facebook 網址</span>
                    <input className={inputClass} placeholder="https://facebook.com/..." value={form.FacebookUrl} onChange={(event) => setField("FacebookUrl", event.target.value)} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>Instagram 網址</span>
                    <input className={inputClass} placeholder="https://instagram.com/..." value={form.InstagramUrl} onChange={(event) => setField("InstagramUrl", event.target.value)} />
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746] sm:col-span-2">
                    <span>YouTube 網址</span>
                    <input className={inputClass} placeholder="https://youtube.com/..." value={form.YoutubeUrl} onChange={(event) => setField("YoutubeUrl", event.target.value)} />
                  </label>
                </div>
              </section>
            </div>

            <aside className="space-y-5">
              <section className="sticky top-[76px] z-10  rounded-md border border-[#dfe6e1] bg-white p-4 md:top-20">
                <h2 className="mb-3 text-lg font-bold text-[#203d32]">儲存</h2>
                {updatedAt && <p className="mb-3 text-xs text-[#9a8a7a]">上次更新：{updatedAt}</p>}
                <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                  <Save size={18} />
                  {saving ? "儲存中" : "儲存公司資料"}
                </button>
              </section>
            </aside>
          </div>
        )}
      </form>
    </AdminLayout>
  );
}
